import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';

// ===== NET2 VR/AR SIMULATION PORTAL =====
// Comprehensive frontend for immersive learning experiences

const SIMULATION_API = `${process.env.REACT_APP_BACKEND_URL}/api/simulation`;

// Device capability detection
const detectDeviceCapabilities = async () => {
  const canvas = document.createElement('canvas');
  const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
  
  const capabilities = {
    device_id: localStorage.getItem('net2_device_id') || generateDeviceId(),
    webxr_supported: 'xr' in navigator,
    ar_supported: 'xr' in navigator && navigator.mediaDevices && navigator.mediaDevices.getUserMedia,
    vr_supported: 'xr' in navigator,
    gpu_tier: gl ? 'medium' : 'low',
    memory_gb: navigator.deviceMemory || 4,
    user_agent: navigator.userAgent
  };
  
  localStorage.setItem('net2_device_id', capabilities.device_id);
  return capabilities;
};

const generateDeviceId = () => {
  return 'device_' + Math.random().toString(36).substr(2, 9) + Date.now().toString(36);
};

// ===== SIMULATION PORTAL MAIN COMPONENT =====
const SimulationPortal = ({ user, onBack }) => {
  const [modules, setModules] = useState([]);
  const [selectedModule, setSelectedModule] = useState(null);
  const [activeSession, setActiveSession] = useState(null);
  const [deviceCapabilities, setDeviceCapabilities] = useState(null);
  const [filters, setFilters] = useState({
    category: '',
    device_type: '',
    difficulty: ''
  });
  const [loading, setLoading] = useState(true);
  const [simulationLoading, setSimulationLoading] = useState(false);

  useEffect(() => {
    initializePortal();
  }, []);

  // Reload modules when filters change
  useEffect(() => {
    if (deviceCapabilities) {
      loadSimulationModules();
    }
  }, [filters]);

  const initializePortal = async () => {
    try {
      // Detect device capabilities
      const capabilities = await detectDeviceCapabilities();
      
      // Register device with simulation backend
      try {
        const deviceResponse = await axios.post(`${SIMULATION_API}/device/detect`, {
          user_agent: capabilities.user_agent,
          device_info: capabilities
        });
        setDeviceCapabilities(deviceResponse.data);
      } catch (deviceError) {
        console.error('Device detection failed, using defaults:', deviceError);
        // Set default capabilities if detection fails
        setDeviceCapabilities({
          device_id: capabilities.device_id,
          capabilities: {
            device_type: 'desktop',
            supports_webxr: capabilities.webxr_supported || false,
            supports_ar: capabilities.ar_supported || false,
            supports_vr: capabilities.vr_supported || false,
            performance_tier: 'medium'
          }
        });
      }
      
      // Load available modules
      await loadSimulationModules();
      
    } catch (error) {
      console.error('Error initializing simulation portal:', error);
      // Set minimal default capabilities
      setDeviceCapabilities({
        device_id: 'default',
        capabilities: {
          device_type: 'desktop',
          supports_webxr: false,
          supports_ar: false,
          supports_vr: false,
          performance_tier: 'medium'
        }
      });
    } finally {
      setLoading(false);
    }
  };

  const loadSimulationModules = async () => {
    try {
      const params = new URLSearchParams();
      Object.keys(filters).forEach(key => {
        if (filters[key]) params.append(key, filters[key]);
      });
      
      const response = await axios.get(`${SIMULATION_API}/modules?${params}`);
      setModules(response.data.modules);
    } catch (error) {
      console.error('Error loading simulation modules:', error);
    }
  };

  const startSimulation = async (module) => {
    setSimulationLoading(true);
    try {
      // For video modules, directly open the video URL without session creation
      if (module.module_type === 'video') {
        window.open(module.webxr_url, '_blank');
        setSimulationLoading(false);
        return;
      }

      // For demo/placeholder modules, show coming soon message
      if (module.webxr_url && (module.webxr_url.includes('/api/simulation/games/') || module.webxr_url.includes('/api/simulation/webxr/'))) {
        alert(`🎮 "${module.title}" is coming soon!\n\nThis simulation experience is currently under development. Check back later for the full interactive experience.`);
        setSimulationLoading(false);
        return;
      }

      const sessionResponse = await axios.post(`${SIMULATION_API}/sessions/start`, {
        user_id: user.id,
        module_id: module.id,
        device_type: deviceCapabilities.capabilities.device_type,
        device_id: deviceCapabilities.device_id
      });
      
      setActiveSession(sessionResponse.data);
      setSelectedModule(module);
      
      // Launch simulation based on type
      if (module.module_type === 'webxr' || module.module_type === '3d_desktop') {
        // Launch in iframe or new window
        window.open(sessionResponse.data.launch_url, '_blank');
      } else if (module.module_type === 'ar') {
        // Launch AR experience
        launchARExperience(sessionResponse.data.launch_url);
      } else if (module.module_type === 'vr') {
        // Provide VR app download link
        showVRLaunchInstructions(module);
      }
      
    } catch (error) {
      console.error('Error starting simulation:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Unknown error';
      alert(`Unable to launch this simulation.\n\nThis experience may still be in development. Please try another module or check back later.`);
    } finally {
      setSimulationLoading(false);
    }
  };

  const launchARExperience = (launchUrl) => {
    // For AR, we'd typically launch in the same window with camera access
    window.location.href = launchUrl;
  };

  const showVRLaunchInstructions = (module) => {
    alert(`VR Module: "${module.title}"\n\nTo experience this simulation:\n1. Put on your VR headset\n2. Launch the NET2 VR app\n3. Select "${module.title}"\n\nIf you don't have the app, download it from your headset's store.`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900 flex items-center justify-center">
        <div className="text-center text-white">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-white mx-auto mb-4"></div>
          <h2 className="text-2xl font-bold mb-2">Initializing Simulation Portal</h2>
          <p>Detecting device capabilities...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900">
      {/* Header */}
      <div className="bg-black bg-opacity-30 backdrop-blur-sm">
        <div className="container mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={onBack}
                className="text-white hover:text-blue-300 transition-colors"
              >
                ← Back to NET2
              </button>
              <div>
                <h1 className="text-3xl font-bold text-white">🥽 Simulation Portal</h1>
                <p className="text-blue-200">Immersive Learning Experiences</p>
              </div>
            </div>
            
            <div className="text-right text-white">
              <p className="text-sm">Welcome, {user?.first_name}</p>
              <p className="text-xs text-blue-200">
                Device: {deviceCapabilities?.capabilities.device_type} | 
                VR: {deviceCapabilities?.capabilities.supports_vr ? '✓' : '✗'} | 
                AR: {deviceCapabilities?.capabilities.supports_ar ? '✓' : '✗'}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        {/* Filters */}
        <div className="bg-white bg-opacity-10 backdrop-blur-sm rounded-xl p-6 mb-8">
          <h3 className="text-xl font-semibold text-white mb-4">Filter Simulations</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <select
              value={filters.category}
              onChange={(e) => setFilters({...filters, category: e.target.value})}
              className="bg-white bg-opacity-20 text-white placeholder-gray-300 border border-white border-opacity-30 rounded-lg px-4 py-2"
            >
              <option value="">All Categories</option>
              <option value="financial_literacy">💰 Financial Literacy</option>
              <option value="mental_health">🧠 Mental Health</option>
              <option value="cultural_immersion">🌍 Cultural Immersion</option>
              <option value="job_interview">💼 Job Interview Practice</option>
            </select>

            <select
              value={filters.device_type}
              onChange={(e) => setFilters({...filters, device_type: e.target.value})}
              className="bg-white bg-opacity-20 text-white placeholder-gray-300 border border-white border-opacity-30 rounded-lg px-4 py-2"
            >
              <option value="">All Devices</option>
              <option value="mobile">Mobile</option>
              <option value="desktop">Desktop</option>
              <option value="vr_headset">VR Headset</option>
            </select>

            <select
              value={filters.difficulty}
              onChange={(e) => setFilters({...filters, difficulty: e.target.value})}
              className="bg-white bg-opacity-20 text-white placeholder-gray-300 border border-white border-opacity-30 rounded-lg px-4 py-2"
            >
              <option value="">All Levels</option>
              <option value="beginner">Beginner</option>
              <option value="intermediate">Intermediate</option>
              <option value="advanced">Advanced</option>
            </select>
          </div>
        </div>

        {/* Simulation Modules Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {modules.map((module) => (
            <SimulationModuleCard
              key={module.id}
              module={module}
              deviceCapabilities={deviceCapabilities}
              onLaunch={() => startSimulation(module)}
              loading={simulationLoading}
            />
          ))}
        </div>

        {modules.length === 0 && (
          <div className="text-center py-16">
            <div className="text-white text-6xl mb-4">🎮</div>
            <h3 className="text-2xl font-bold text-white mb-2">No Simulations Found</h3>
            <p className="text-blue-200">Try adjusting your filters or check back later for new experiences.</p>
          </div>
        )}
      </div>

      {/* Device Compatibility Info */}
      <div className="bg-black bg-opacity-30 backdrop-blur-sm mt-16">
        <div className="container mx-auto px-4 py-8">
          <h3 className="text-xl font-semibold text-white mb-4">Your Device Capabilities</h3>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-center">
            <div className="text-white">
              <div className="text-3xl mb-2">📱</div>
              <p className="font-semibold">Mobile Ready</p>
              <p className="text-sm text-blue-200">AR & 3D Experiences</p>
            </div>
            <div className="text-white">
              <div className="text-3xl mb-2">💻</div>
              <p className="font-semibold">Desktop Compatible</p>
              <p className="text-sm text-blue-200">WebXR & 3D Simulations</p>
            </div>
            <div className={`${deviceCapabilities?.capabilities.supports_vr ? 'text-green-400' : 'text-gray-400'}`}>
              <div className="text-3xl mb-2">🥽</div>
              <p className="font-semibold">VR Support</p>
              <p className="text-sm">
                {deviceCapabilities?.capabilities.supports_vr ? 'Available' : 'Not Detected'}
              </p>
            </div>
            <div className={`${deviceCapabilities?.capabilities.supports_ar ? 'text-green-400' : 'text-gray-400'}`}>
              <div className="text-3xl mb-2">📲</div>
              <p className="font-semibold">AR Support</p>
              <p className="text-sm">
                {deviceCapabilities?.capabilities.supports_ar ? 'Available' : 'Not Detected'}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ===== SIMULATION MODULE CARD COMPONENT =====
const SimulationModuleCard = ({ module, deviceCapabilities, onLaunch, loading }) => {
  const isCompatible = module.supported_devices.includes(deviceCapabilities?.capabilities.device_type);
  
  const getModuleIcon = (moduleType) => {
    switch (moduleType) {
      case 'webxr': return '🌐';
      case 'ar': return '📲';
      case 'vr': return '🥽';
      case '3d_desktop': return '🖥️';
      case 'video': return '📹';
      default: return '🎮';
    }
  };

  const getCategoryColor = (category) => {
    switch (category) {
      case 'financial_literacy': return 'from-green-500 to-emerald-600';
      case 'cultural_immersion': return 'from-blue-500 to-cyan-600';
      case 'skill_training': return 'from-purple-500 to-violet-600';
      case 'mental_health': return 'from-pink-500 to-rose-600';
      default: return 'from-gray-500 to-slate-600';
    }
  };

  return (
    <div className={`bg-gradient-to-br ${getCategoryColor(module.category)} rounded-xl p-6 text-white transform transition-all duration-300 hover:scale-105 ${!isCompatible ? 'opacity-60' : ''}`}>
      <div className="flex items-center justify-between mb-4">
        <div className="text-4xl">{getModuleIcon(module.module_type)}</div>
        <div className="flex items-center space-x-2">
          <span className="bg-white bg-opacity-20 px-2 py-1 rounded text-xs font-medium">
            {module.difficulty_level}
          </span>
          <span className="bg-white bg-opacity-20 px-2 py-1 rounded text-xs">
            {module.estimated_duration}min
          </span>
        </div>
      </div>

      <h3 className="text-xl font-bold mb-2">{module.title}</h3>
      <p className="text-white text-opacity-90 text-sm mb-4 line-clamp-3">
        {module.description}
      </p>

      {/* Learning Objectives */}
      {module.learning_objectives && module.learning_objectives.length > 0 && (
        <div className="mb-4">
          <p className="text-xs font-semibold mb-2">Learning Objectives:</p>
          <ul className="text-xs space-y-1">
            {module.learning_objectives.slice(0, 2).map((objective, index) => (
              <li key={index} className="flex items-start">
                <span className="mr-2">•</span>
                <span>{objective}</span>
              </li>
            ))}
            {module.learning_objectives.length > 2 && (
              <li className="text-white text-opacity-70">
                +{module.learning_objectives.length - 2} more...
              </li>
            )}
          </ul>
        </div>
      )}

      {/* Device Compatibility */}
      <div className="mb-4">
        <p className="text-xs font-semibold mb-2">Compatible Devices:</p>
        <div className="flex space-x-2">
          {module.supported_devices.map((device) => (
            <span
              key={device}
              className={`text-xs px-2 py-1 rounded ${
                device === deviceCapabilities?.capabilities.device_type
                  ? 'bg-green-500 bg-opacity-80'
                  : 'bg-white bg-opacity-20'
              }`}
            >
              {device.replace('_', ' ')}
            </span>
          ))}
        </div>
      </div>

      {/* Launch Button */}
      <button
        onClick={onLaunch}
        disabled={!isCompatible || loading}
        className={`w-full py-3 rounded-lg font-semibold transition-all ${
          isCompatible
            ? 'bg-white text-gray-900 hover:bg-gray-100'
            : 'bg-gray-600 text-gray-300 cursor-not-allowed'
        }`}
      >
        {loading ? (
          <span className="flex items-center justify-center">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-900 mr-2"></div>
            Launching...
          </span>
        ) : isCompatible ? (
          `Launch ${module.module_type.toUpperCase()} Experience`
        ) : (
          'Device Not Compatible'
        )}
      </button>

      {!isCompatible && (
        <p className="text-xs text-white text-opacity-70 mt-2 text-center">
          This simulation requires: {module.supported_devices.join(', ')}
        </p>
      )}
    </div>
  );
};

// ===== AR CAMERA COMPONENT =====
const ARCameraView = ({ module, sessionId, onComplete }) => {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [isActive, setIsActive] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    initializeCamera();
    return () => {
      stopCamera();
    };
  }, []);

  const initializeCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment' }
      });
      
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        setIsActive(true);
      }
    } catch (err) {
      setError('Camera access denied. AR experience requires camera permission.');
    }
  };

  const stopCamera = () => {
    if (videoRef.current?.srcObject) {
      const tracks = videoRef.current.srcObject.getTracks();
      tracks.forEach(track => track.stop());
    }
  };

  if (error) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center text-white text-center p-4">
        <div>
          <div className="text-6xl mb-4">📷</div>
          <h2 className="text-2xl font-bold mb-2">Camera Access Required</h2>
          <p className="mb-4">{error}</p>
          <button
            onClick={onComplete}
            className="bg-blue-600 text-white px-6 py-2 rounded-lg"
          >
            Return to Portal
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black relative">
      <video
        ref={videoRef}
        autoPlay
        playsInline
        muted
        className="w-full h-full object-cover"
      />
      
      {/* AR Overlay UI */}
      <div className="absolute top-4 left-4 right-4 z-10">
        <div className="bg-black bg-opacity-70 text-white p-4 rounded-lg">
          <h2 className="text-xl font-bold mb-2">{module.title}</h2>
          <p className="text-sm mb-2">AR Experience Active</p>
          <div className="flex space-x-4">
            <button
              onClick={onComplete}
              className="bg-red-600 text-white px-4 py-2 rounded text-sm"
            >
              Exit AR
            </button>
          </div>
        </div>
      </div>
      
      {/* AR Content Canvas */}
      <canvas
        ref={canvasRef}
        className="absolute top-0 left-0 w-full h-full pointer-events-none"
      />
    </div>
  );
};

// ===== VR INSTRUCTION COMPONENT =====
const VRInstructions = ({ module, onClose }) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl p-8 max-w-md w-full mx-4">
        <div className="text-center">
          <div className="text-6xl mb-4">🥽</div>
          <h2 className="text-2xl font-bold mb-4">VR Experience Ready</h2>
          <p className="text-gray-600 mb-6">"{module.title}"</p>
          
          <div className="text-left space-y-4 mb-6">
            <div className="flex items-center space-x-3">
              <span className="bg-blue-100 text-blue-800 w-6 h-6 rounded-full flex items-center justify-center text-sm font-bold">1</span>
              <span>Put on your VR headset</span>
            </div>
            <div className="flex items-center space-x-3">
              <span className="bg-blue-100 text-blue-800 w-6 h-6 rounded-full flex items-center justify-center text-sm font-bold">2</span>
              <span>Launch the NET2 VR app</span>
            </div>
            <div className="flex items-center space-x-3">
              <span className="bg-blue-100 text-blue-800 w-6 h-6 rounded-full flex items-center justify-center text-sm font-bold">3</span>
              <span>Select "{module.title}"</span>
            </div>
          </div>
          
          <div className="space-y-3">
            <button className="w-full bg-blue-600 text-white py-3 rounded-lg font-semibold">
              Download NET2 VR App
            </button>
            <button
              onClick={onClose}
              className="w-full bg-gray-200 text-gray-800 py-3 rounded-lg font-semibold"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SimulationPortal;